import React, { useContext, createContext, useEffect, useReducer } from 'react';

import userApi from '~/account/api/user';
import UserReducer from '~/reducers/user';
import { handleLoginCartSync } from '~/utilities/cartActions';
import useLocations from '~/hooks/useLocations';

import {
    setDefaultVehicleId as setDefaultVehicleIdLocal,
    isLoggedIn as localIsLoggedIn,
    getAccessToken,
    getRefreshToken,
    saveTokens,
    resetTokens
} from '~/account/utilities/helpers';

import recaptchaVerify from '~/utilities/recaptchaVerify';

const UserContext = createContext();

const initialUserState = {
    loggedIn: false,
    loading: true,
    user: {},
    accessToken: null,
    refreshToken: null,
    defaultVehicleId: null,
    error: null
};

const mapUserResponse = (apiUser) => {
    const name = [apiUser.first_name, apiUser.last_name]
        .filter(Boolean)
        .join(' ');
    return {
        id: apiUser.id,
        name: name || apiUser.email,
        email: apiUser.email,
        phone_number: apiUser.phone,
        is_sms_verified: apiUser.is_verified,
        zip_code_billing: apiUser.zip_code,
        birthdate: apiUser.date_of_birth,
        is_receiving_promotion_texts: apiUser.push_notifications_enabled,
        is_receiving_promotion_emails: apiUser.email_notifications_enabled,
        stripe_customer_id: apiUser.stripe_customer_id || null
    };
};

export const getUser = async () => {
    const { data, errorMessage } = await userApi.getCurrentUser();

    if (errorMessage) {
        return {
            error: errorMessage
        };
    } else if (data) {
        return mapUserResponse(data);
    }
    return {
        error: 'An unknown error occurred.'
    };
};

export const UserContextProvider = ({ children }) => {
    const [state, dispatch] = useReducer(UserReducer, initialUserState);
    const locations = useLocations();

    useEffect(() => {
        let didCancel = false;
        async function initialLogin() {
            if (didCancel) {
                return;
            }
            const userData = await getUser();
            if (userData.error) {
                if (!localIsLoggedIn()) {
                    dispatch({ type: 'LOGOUT' });
                } else {
                    dispatch({ type: 'ERROR', error: userData.error });
                }
                return;
            }

            await handleLoginCartSync(userData, locations);

            dispatch({
                type: 'LOGIN',
                user: userData,
                accessToken: getAccessToken(),
                refreshToken: getRefreshToken()
            });
        }
        if (localIsLoggedIn()) {
            dispatch({ type: 'LOADING' });
            initialLogin();
        } else {
            dispatch({ type: 'LOADED' });
        }
        return () => {
            didCancel = true;
        };
    }, []);

    useEffect(() => {
        setDefaultVehicleIdLocal(state.defaultVehicleId);
    }, [state.defaultVehicleId]);

    return (
        <UserContext.Provider
            value={{
                isLoggedIn: state.loggedIn,
                loading: state.loading,
                user: state.user,
                userError: state.error,
                refreshUser: async () => {
                    const user = await getUser();
                    if (user.error) {
                        if (!localIsLoggedIn()) {
                            dispatch({ type: 'LOGOUT' });
                        } else {
                            dispatch({ type: 'ERROR', error: user.error });
                        }
                    } else {
                        dispatch({ type: 'UPDATE_USER', user });
                    }
                },
                defaultVehicleId: state.defaultVehicleId,
                setDefaultVehicleId: (vehicleId) => {
                    dispatch({ type: 'UPDATE_DEFAULT_VEHICLE', vehicleId });
                },
                login: async (username, password) => {
                    const token = await recaptchaVerify();
                    const signInResponse = await userApi.signIn(
                        username,
                        password,
                        token
                    );

                    const { success, data } = signInResponse;

                    if (success) {
                        saveTokens(data);
                        const user = mapUserResponse(data);
                        await handleLoginCartSync(user, locations);

                        dispatch({
                            type: 'LOGIN',
                            user,
                            accessToken: data.access,
                            refreshToken: data.refresh
                        });
                    }

                    return signInResponse;
                },
                logout: async () => {
                    await userApi.signOut(state.refreshToken);
                    resetTokens();
                    dispatch({ type: 'LOGOUT' });
                    return true;
                }
            }}
        >
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => useContext(UserContext);
